import React, { useState, useEffect } from "react";
import ApiClient from "../api/ApiClient";

// "Models with recent signals" — lists every model with its most recent
// signal files, one row per signal tag. All signals returned by the API
// come from the latest correlation id (calculation) for the model.

const pageSizeOptions = [10, 25, 100];

const orderByOptions = [
  { value: "IdDesc", label: "ID (Desc)" },
  { value: "Id", label: "ID (Asc)" },
  { value: "Name", label: "Name (Asc)" },
  { value: "NameDesc", label: "Name (Desc)" },
  { value: "CreateTimeDesc", label: "Create Time (Desc)" },
  { value: "CreateTime", label: "Create Time (Asc)" },
];

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function SignalActionBadge({ action }) {
  const colors = {
    Buy: "success",
    Sell: "danger",
    Money: "secondary",
  };
  return (
    <span className={`badge bg-${colors[action] || "dark"} me-1`}>{action}</span>
  );
}

// One row = one model + one signal tag. The signals shown are the most
// recent N produced by the latest signal file for that tag in the latest
// correlation.
function ModelRecentSignalsRow({ item, tag, signals }) {
  const hasSignals = signals.length > 0;
  const defaultOpen = hasSignals && signals.length <= 5;

  return (
    <tr>
      <td>{item.ModelId}</td>
      <td>
        <div className="fw-semibold">{item.ModelName || `Model #${item.ModelId}`}</div>
        <code className="small text-muted">{item.ModelFileName || "—"}</code>
      </td>
      <td>
        {tag
          ? <span className="badge bg-info text-dark">{tag}</span>
          : <span className="text-muted small">—</span>}
      </td>
      <td>
        {item.LatestCorrelationId
          ? <code className="small">{item.LatestCorrelationId}</code>
          : <span className="text-muted small">—</span>}
      </td>
      <td style={{ minWidth: 320 }}>
        {!hasSignals && (
          <span className="text-muted small">No signals</span>
        )}
        {hasSignals && (
          <details open={defaultOpen}>
            <summary className="small text-muted">
              {signals.length} signal{signals.length === 1 ? "" : "s"} · click to {defaultOpen ? "collapse" : "expand"}
            </summary>
            <div style={{ maxHeight: 220, overflowY: "auto", marginTop: 6 }}>
              <table className="table table-sm table-striped mb-0">
                <thead className="table-light sticky-top">
                  <tr>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {signals.map((s, i) => (
                    <tr key={i}>
                      <td className="small text-nowrap">{formatDateTime(s.Time)}</td>
                      <td><SignalActionBadge action={s.Action} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
      </td>
    </tr>
  );
}

export const ModelsWithRecentSignals = () => {
  const [name, setName] = useState("");
  const [publicOnly, setPublicOnly] = useState("");
  const [orderBy, setOrderBy] = useState("IdDesc");
  const [limitSignals, setLimitSignals] = useState(10);

  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(0);

  const [total, setTotal] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function fetchData() {
    setLoading(true);
    setError(null);
    const body = {
      NamePartial: name || undefined,
        Public: publicOnly === "" ? undefined : publicOnly === "true",
        OrderBy: orderBy,
        LimitSignals: limitSignals,
        Limit: pageSize,
        Offset: page * pageSize,
      };
    try {
      const data = await ApiClient.getRecentSignals(body);
      setItems(data.Items || []);
      setTotal(data.TotalCount || 0);
    } catch (err) {
      console.error("Failed to load recent models", err);
      setError(err.message || "Failed to load data");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, orderBy, limitSignals]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function onSearch(e) {
    e.preventDefault();
    setPage(0);
    fetchData();
  }

  return (
    <div>
      <h1>Models with recent signals</h1>
      <p className="text-muted">
        Per-model list of the most recent signals, one row per signal tag.
        All signals come from the latest correlation id (calculation) for
        each model.
      </p>

      <form onSubmit={onSearch} className="mb-3">
        <div style={{ maxWidth: "900px" }}>
          <div className="row">
            <div className="col-md-3 mb-2">
              <label className="col-form-label">Name (partial)</label>
              <input
                placeholder="Name"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div className="col-md-2 mb-2">
              <label className="col-form-label">Public</label>
              <select
                className="form-control"
                value={publicOnly}
                onChange={(e) => setPublicOnly(e.target.value)}
              >
                <option value="">&lt;Any&gt;</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div className="col-md-3 mb-2">
              <label className="col-form-label">Order By</label>
              <select
                className="form-control"
                value={orderBy}
                onChange={(e) => { setOrderBy(e.target.value); setPage(0); }}
              >
                {orderByOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2 mb-2">
              <label className="col-form-label">Signals / tag</label>
              <select
                className="form-control"
                value={limitSignals}
                onChange={(e) => { setLimitSignals(Number(e.target.value)); setPage(0); }}
              >
                {[5, 10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2 mb-2 d-flex align-items-end">
              <button className="btn btn-primary w-100" type="submit">Search</button>
            </div>
          </div>
        </div>
      </form>

      <div className="mb-2 d-flex align-items-center">
        <div className="me-2">Page size:</div>
        <select
          className="form-control me-3"
          style={{ width: "120px" }}
          value={pageSize}
          onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
        >
          {pageSizeOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div>Page: {page + 1} / {totalPages || 1}</div>
      </div>

      <div className="mb-3 d-flex" style={{ gap: "8px", alignItems: "center" }}>
        <button
          className="btn btn-secondary"
          disabled={page <= 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </button>
        <button
          className="btn btn-secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        >
          Next
        </button>
      </div>

      {loading && <p><em>Loading...</em></p>}
      {error && <div className="alert alert-danger">{error}</div>}
      {!loading && !error && (!items || items.length === 0) && (
        <p>No models found.</p>
      )}
      {!loading && !error && items && items.length > 0 && (
        <div className="table-responsive">
          <table className="table table-striped align-middle">
            <thead>
              <tr>
                <th>Id</th>
                <th>Model</th>
                <th>Tag</th>
                <th>Correlation ID</th>
                <th>Recent signals</th>
              </tr>
            </thead>
            <tbody>
              {items.flatMap((item) => {
                const recentByTag = item.RecentSignals || {};
                const tags = Object.keys(recentByTag);
                if (tags.length === 0) {
                  // No signal files at all — render a single placeholder row
                  // so the model is still visible in the list.
                  return [
                    <ModelRecentSignalsRow
                      key={`${item.ModelId}-__none__`}
                      item={item}
                      tag=""
                      signals={[]}
                    />,
                  ];
                }
                return tags.map((tag) => (
                  <ModelRecentSignalsRow
                    key={`${item.ModelId}-${tag}`}
                    item={item}
                    tag={tag}
                    signals={recentByTag[tag] || []}
                  />
                ));
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-2 d-flex" style={{ gap: "8px" }}>
        <button
          className="btn btn-secondary"
          disabled={page <= 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          Previous
        </button>
        <button
          className="btn btn-secondary"
          disabled={page + 1 >= totalPages}
          onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ModelsWithRecentSignals;